// Rewrites links authored against docs.curri.com so they resolve inside the portal.
//
// The docs carry ~130 cross-links, and almost all of them point at one of the two
// appendices with a `#anchor`. Those anchors are quoted from inside customer ERP
// installs, so "close enough" is not a bar this code gets to clear — hence the
// coverage test in tests/docs-links.unit.test.ts, which walks every link in every
// page and asserts it lands on a real page and a real heading.
//
// Three shapes appear in the content:
//   ./appendix#deliverymethod            relative, resolved against the page URL
//   ./08-appendix.md                     relative, old numeric filename
//   /docs/queries-and-mutations/appendix root-absolute, old site layout
//
// Relative links are resolved against the page's *new* path rather than mapped.
// That works because the slug layout was preserved exactly in the move — including
// `getting-started`, which was a frontmatter slug override on docs.curri.com and is
// a slug override in the manifest for the same reason.

/**
 * docs.curri.com path prefix → portal path prefix.
 *
 * docs.curri.com now redirects here with these same prefixes, encoded in
 * `apps/curri-docs/vercel.json` and pinned by `tests/docs-redirects.unit.test.ts`.
 * The table still rewrites in-content links authored against the old site's
 * layout, so following one lands on the portal page directly rather than going
 * through the redirect.
 */
export const PATH_REWRITES: ReadonlyArray<readonly [string, string]> = [
  ['/docs/eclipse-integration', '/docs/eclipse'],
  ['/docs/delivery-service-providers', '/docs/dsp'],
  ['/docs/getting-started', '/docs/graphql/getting-started'],
  ['/docs/queries-and-mutations', '/docs/graphql/queries-and-mutations'],
  ['/docs/itineraries', '/docs/graphql/itineraries'],
  ['/docs/webhooks-errors-statuses', '/docs/graphql/webhooks-errors-statuses'],
  // Images moved under the docs namespace so the service can reserve one prefix
  // for them instead of claiming `/img` at the site root.
  ['/img/', '/docs/img/'],
]

const DOCS_ORIGIN = 'docs.curri.com'

/** Docusaurus derived ids by dropping `NN-` from each segment and the extension. */
export const stripDocusaurusFilename = (path: string): string =>
  path
    .replace(/\.mdx?($|#)/, '$1')
    .split('/')
    .map(segment => segment.replace(/^\d+-/, ''))
    .join('/')

/** Applies the prefix table to an already-absolute, already-de-numbered path. */
export const rewritePath = (path: string): string => {
  for (const [from, to] of PATH_REWRITES) {
    if (path === from) return to
    const boundary = from.endsWith('/') ? from : `${from}/`
    if (path.startsWith(boundary)) return to + path.slice(from.length)
  }
  // Bare `/docs` was the welcome page (frontmatter `slug: /`).
  if (path === '/docs') return '/docs/graphql'
  return path
}

const splitHash = (href: string): [string, string] => {
  const index = href.indexOf('#')
  return index === -1 ? [href, ''] : [href.slice(0, index), href.slice(index)]
}

/**
 * Resolve a link found in `pagePath`'s markdown to a portal path.
 *
 * External links, `mailto:`, and bare `#anchor` links come back untouched.
 */
export const rewriteHref = (href: string, pagePath: string): string => {
  if (!href || href.startsWith('#')) return href

  // Protocol-absolute. Only docs.curri.com is ours to rewrite; everything else
  // is somebody's real URL and must survive verbatim.
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) {
    let parsed: URL
    try {
      parsed = new URL(href)
    } catch {
      return href
    }
    if (parsed.hostname !== DOCS_ORIGIN) return href
    return rewritePath(stripDocusaurusFilename(parsed.pathname)) + parsed.hash
  }

  const [rawPath, hash] = splitHash(href)

  if (rawPath.startsWith('/')) {
    return rewritePath(stripDocusaurusFilename(rawPath)) + hash
  }

  // Relative. Resolve against the page's URL the way a browser would — the
  // directory of the current path, not the file — then leave it alone: the
  // result is already in portal space.
  const base = new URL(pagePath, 'https://portal.invalid')
  const resolved = new URL(stripDocusaurusFilename(rawPath), base)
  return resolved.pathname + hash
}

/** Images were authored as `/img/foo.png`; they ship under `/docs/img/`. */
export const rewriteImageSrc = (src: string): string => {
  if (!src || /^[a-z][a-z0-9+.-]*:/i.test(src)) return src
  return rewritePath(src)
}

/** True for links the SPA should route rather than hand to the browser. */
export const isInternalHref = (href: string): boolean =>
  href.startsWith('/') && !href.startsWith('//')
